import React, { useRef, useState } from "react";
import { Send, Table, X } from "lucide-react";
import i18n from "@/i18n";
import { Trans } from "react-i18next";

import DialogModal from "@/components/dialogModal";
import { ItemList, ItemListColumn, ItemListRow } from "@/components/itemList";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PrepaidPanel from "@/components/subscriptions/prepaidPanel";
import UserCommRecipientPlaceholderValuesAlert from "./userCommRecipientPlaceholderValuesAlert";
import UserCommRecipientPlaceholderValuesGrid from "./userCommRecipientPlaceholderValuesGrid";

import useDialogModal from "@/hooks/useDialogModal";
import useUserComm from "@/hooks/notifications/useUserComm";
import useCreateSendEmailJobs from "@/hooks/notifications/useCreateSendEmailJobs";
import useSendEmailJobRecords from "@/hooks/notifications/useSendEmailJobRecords";
import useUpdateUserComm from "@/hooks/notifications/useUpdateUserComm";
import { PRICE_PER_EMAIL } from "@/hooks/notifications/useUserComm";
import useSendEmailJobRecipientPlaceholderValues from "@/hooks/notifications/useSendEmailJobRecipientPlaceholderValues";

const { t } = i18n;

const UserCommSendPanel = ({ userCommId, onCancel, onComplete, editor }) => {
  const { dialogElement, confirm } = useDialogModal();

  const userCommRecipientPlaceholderValuesRef = useRef(null);

  const { data: userCommQuery, isLoading: isLoadingUserComm } =
    useUserComm(userCommId);
  const userComm = userCommQuery?.data;
  const userCommRecipientsById = userComm?.recipients.reduce(
    (acc, r) => ({ ...acc, [r.id]: r }),
    {},
  );

  const {
    mutateAsync: createSendEmailJobs,
    error,
    isPending: isCreatingSendEmailJobs,
  } = useCreateSendEmailJobs("userComm", userCommId);
  const createSendEmailJobsError = error?.response?.data;

  const { data: sendEmailJobRecords, isLoading: isLoadingSendEmailJobRecords } =
    useSendEmailJobRecords(userComm.recipients, editor);

  const { missingPlaceholderValuesCount } =
    useSendEmailJobRecipientPlaceholderValues(userComm.recipients, editor);

  const { mutateAsync: updateUserComm } = useUpdateUserComm(userCommId);

  const isLoading = isLoadingUserComm || isLoadingSendEmailJobRecords;

  const sendableEmailJobs = sendEmailJobRecords.filter(
    (record) => record.email?.length > 1,
  );
  const sendableEmailCount = sendableEmailJobs.length;

  const [showPlaceholderStatusGrid, setShowPlaceholderStatusGrid] =
    useState(false);

  return (
    <DialogModal
      open
      size="sm"
      onCancel={onCancel}
      isLoading={isLoading}
      title={t("user_comms.send_panel.confirmation.title")}
      submitText={
        <>
          <Send size={18} />
          {t("user_comms.send_panel.actions.send.label", {
            count: sendableEmailCount,
          })}
        </>
      }
      isSubmitting={isCreatingSendEmailJobs}
      onSubmit={async () => {
        if (missingPlaceholderValuesCount > 0) {
          // Show an error message or handle the case where there are missing placeholders
          const isConfirmed = await confirm(
            <Trans
              i18nKey="send_email_jobs.placeholder_status.missing_placeholder_values.body"
              values={{ count: missingPlaceholderValuesCount }}
            />,
            <Trans i18nKey="send_email_jobs.placeholder_status.missing_placeholder.confirm_send.body" />,
            {
              variant: "destructive",
              submitText: t("generic.send_anyway"),
              footerRenderer: (props, submitValue) => (
                <>
                  {props.cancelButtonRenderer?.(props, submitValue)}
                  <Button
                    variant="secondary"
                    onClick={() => setShowPlaceholderStatusGrid(true)}
                  >
                    <Table size={16} />{" "}
                    {t("send_email_jobs.placeholder_status.view_issues", {
                      count: missingPlaceholderValuesCount,
                    })}
                  </Button>
                  {props.submitButtonRenderer?.(props, submitValue)}
                </>
              ),
            },
          );
          if (!isConfirmed) return;
        }

        const jobs = sendableEmailJobs.map((job) => ({
          recipientName: job.name,
          email: job.email,
          subject: userComm.subject,
          body: job.body,
          recipientReference: {
            type: job.recordType,
            id: job.id,
          },
          replyToEmail: userComm.replyToEmail,
        }));

        await createSendEmailJobs(jobs, { onSuccess: onComplete });
      }}
      bodyRenderer={() => {
        const validationErrors = createSendEmailJobsError?.errors?.filter(
          (error) => userCommRecipientsById[error.recipientReference?.id],
        );

        const hasValidationErrors =
          validationErrors?.length > 0 &&
          createSendEmailJobsError?.name === "SendEmailJobsValidationError";

        return (
          <div className="flex flex-col gap-8">
            {showPlaceholderStatusGrid && (
              <UserCommRecipientPlaceholderValuesGrid
                recipients={userComm.recipients}
                editor={editor}
                onCancel={() => setShowPlaceholderStatusGrid(false)}
              />
            )}
            <UserCommRecipientPlaceholderValuesAlert
              ref={userCommRecipientPlaceholderValuesRef}
              userCommId={userCommId}
              editor={editor}
              onClick={() => setShowPlaceholderStatusGrid(true)}
            />
            {hasValidationErrors ? (
              <div className="flex flex-col gap-4">
                <div>
                  {t("user_comms.send_panel.has_validation_errors.message")}
                </div>
                <ItemList columnSizing="1fr 1fr 40px" rightAlignLastColumn>
                  {validationErrors?.map((error) => (
                    <ItemListRow key={error.id}>
                      <ItemListColumn>{error.email}</ItemListColumn>
                      <ItemListColumn className="flex gap-2">
                        {Object.values(error.errors).map((e) => (
                          <Badge
                            size="sm"
                            variant="outline-destructive"
                            key={e.path}
                          >
                            {t([
                              `send_email_job.validation_errors.${e.path}`,
                              "send_email_job.validation_errors.unspecified",
                            ])}
                          </Badge>
                        ))}
                      </ItemListColumn>
                      <ItemListColumn>
                        <Button
                          title={t("user_comms.actions.remove_recipient.title")}
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            updateUserComm({
                              recipients: userComm.recipients.filter(
                                (r) => r.id !== error.recipientReference?.id,
                              ),
                            });
                          }}
                        >
                          <X />
                        </Button>
                      </ItemListColumn>
                    </ItemListRow>
                  ))}
                </ItemList>
              </div>
            ) : (
              <div>
                <Trans
                  i18nKey="user_comms.send_panel.confirmation"
                  values={{ count: sendableEmailCount }}
                />
              </div>
            )}
            <p>
              <Trans
                i18nKey="user_comms.price_per_email"
                values={{ price: `${PRICE_PER_EMAIL} SEK` }}
              />
            </p>
            <div>
              <PrepaidPanel
                className="rounded-xl border p-4"
                pendingAdjustment={sendableEmailCount * -PRICE_PER_EMAIL}
              />
            </div>
            {dialogElement}
          </div>
        );
      }}
    />
  );
};

export default UserCommSendPanel;
